import {
  IsDateString,
  IsIn,
  IsInt,
  IsOptional,
  IsString,
  Max,
  MaxLength,
  Min,
  MinLength,
  ValidateIf,
} from 'class-validator';

/**
 * The s. 144(9) emergency bypass.
 *
 * Until now this was a single boolean on the classification slice, which could
 * record *that* an emergency was declared but nothing about it — not the danger
 * relied on, not who declared it, not which route the file took, and not the
 * compressed timeline that is the whole practical consequence.
 *
 * ── The dual route ──────────────────────────────────────────────────────────
 * The v47 flowchart gives the bypass two exits, and says they "may proceed
 * simultaneously":
 *
 *   → Step 5 — the Form 4 still issues and is served, on a shortened timeline
 *   → Step 8 — direct to work execution, where work is needed to remove the
 *              danger before any notice period could run
 *
 * The best practices spec says the same in prose (Step 2 scenario e.ii): the
 * officer ensures the work is carried out "either by the municipality (proceed
 * to Step 8) or by the owner or occupant, and that a notice to comply is
 * completed and served (proceed to Step 5)". So this is modelled as a repeatable
 * record with an entry point, not a single flag: one file can hold a Step 5
 * declaration and a Step 8 declaration at once, which is exactly the case the
 * boolean could not express.
 *
 * ── The compressed deadline ─────────────────────────────────────────────────
 * **The spec does not prescribe a length, and none is invented here.** s. 144(9)
 * removes the requirement to give sufficient notice and to attend at a
 * reasonable hour — both about *inspection*. It says nothing about how long the
 * owner then has to comply. The flowchart is explicit that the compliance
 * deadline "may be shortened" and "may be adjusted": discretionary, not a fixed
 * statutory period. The only fixed periods in this workflow are the 14-day
 * appeal (s. 134(1)), the 30-day discharge certificate (s. 135(4)) and the
 * six-month proceedings limitation (POPA s. 21(1)).
 *
 * So the compressed period is officer-set and must be justified in writing. The
 * clock derived from it is real and appears beside the statutory ones, but its
 * length is a recorded decision rather than a constant this code asserts.
 */

/** Which route the bypass was entered from. */
export type EmergencyEntryPoint = 'step_5' | 'step_8';
export const EMERGENCY_ENTRY_POINTS: EmergencyEntryPoint[] = ['step_5', 'step_8'];

/**
 * The limb of the statutory definition relied on. The Local Governance Act
 * defines "emergency" as a situation where there is imminent danger to public
 * safety, **or** where land, a building or structure is in imminent danger of
 * suffering serious harm. Which limb is relied on is the substance of the
 * declaration, so it is recorded rather than left to prose.
 */
export type EmergencyDangerBasis = 'public_safety' | 'serious_harm';
export const EMERGENCY_DANGER_BASES: EmergencyDangerBasis[] = ['public_safety', 'serious_harm'];

/** Who carries out the work — spec Step 2 scenario e.ii. */
export type EmergencyWorkBy = 'municipality' | 'owner';
export const EMERGENCY_WORK_BY: EmergencyWorkBy[] = ['municipality', 'owner'];

/**
 * Upper bound on an officer-set compressed period. Not a statutory figure:
 * it is a sanity bound, chosen because a "compressed" period longer than the
 * ordinary 14-day appeal window is not a compression at all and almost
 * certainly a typo. The API rejects it rather than deriving a clock that would
 * quietly misstate the file.
 */
export const EMERGENCY_COMPLIANCE_MAX_DAYS = 14;

export interface EmergencyBypassDetail {
  id: string;
  fileId: string;
  enteredFrom: EmergencyEntryPoint;
  dangerBasis: EmergencyDangerBasis;
  /** What the danger actually is. Required — a bare flag proves nothing. */
  dangerDescription: string;
  workBy: EmergencyWorkBy;
  /**
   * The compressed period, in days from the declaration. Officer-set: the spec
   * prescribes no length. Null where the work is the municipality's to do and
   * no period is being given to anyone.
   */
  complianceDays: number | null;
  /** Why this period is the right one — required whenever a period is set. */
  complianceRationale: string | null;
  /** Derived and stored so the record does not move if the row is edited. */
  complianceDeadline: string | null;
  declaredAt: string;
  declaredById: string | null;
  /**
   * Snapshot of the declaring officer's name, not a join — a declaration under
   * s. 144(9) must keep naming who made it. Mirrors the Form 4 override log.
   */
  declaredByName: string | null;
  /**
   * Withdrawn declarations are kept, never deleted: a bypass that turned out to
   * be unfounded is part of the file's history, and its clock stops rather than
   * disappearing.
   */
  revokedAt: string | null;
  revokedReason: string | null;
  notes: string | null;
}

export class DeclareEmergencyBypassDto {
  @IsIn(EMERGENCY_ENTRY_POINTS)
  enteredFrom!: EmergencyEntryPoint;

  @IsIn(EMERGENCY_DANGER_BASES)
  dangerBasis!: EmergencyDangerBasis;

  // Deliberately not optional and not trivially satisfiable: this is the
  // justification for bypassing the ordinary notice requirements.
  @IsString()
  @MinLength(10)
  @MaxLength(2000)
  dangerDescription!: string;

  @IsIn(EMERGENCY_WORK_BY)
  workBy!: EmergencyWorkBy;

  @IsOptional()
  @IsInt()
  @Min(1)
  @Max(EMERGENCY_COMPLIANCE_MAX_DAYS)
  complianceDays?: number;

  // Required whenever a compressed period is set. The period has no statutory
  // source, so the file has to carry the officer's reason for choosing it.
  @ValidateIf((o) => o.complianceDays !== undefined && o.complianceDays !== null)
  @IsString()
  @MinLength(10)
  @MaxLength(2000)
  complianceRationale?: string;

  /** Defaults to now; accepted so a declaration made in the field can be dated. */
  @IsOptional()
  @IsDateString()
  declaredAt?: string;

  @IsOptional()
  @IsString()
  @MaxLength(2000)
  notes?: string;
}

export class RevokeEmergencyBypassDto {
  @IsString()
  @MinLength(10)
  @MaxLength(2000)
  reason!: string;
}
